import { Component, OnInit, OnDestroy, effect, inject, ChangeDetectionStrategy, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { CommonModule, Location } from '@angular/common';
import { forkJoin, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ToastService } from '@olv/ui-shared/ui-shared/toast/toast.service';
import { LoadingSpinnerComponent } from '@olv/ui-shared/ui-shared/loading-spinner/loading-spinner.component';
import { HasPermissionDirective } from '@olv/ui-shared/directives/has-permission.directive';
import { Merchant } from '@olv/ui-shared/models/merchant.model';
import { PagedResultDto } from '@olv/ui-shared/models/paged-result.dto';
import { Resources, Scopes } from '@olv/ui-shared/models/settlement-permissions.model';
import {
  WalletDto,
  WalletBalances,
  WalletStatusEnum,
  WalletFilterParams,
  WalletTypeEnum,
} from '@olv/ui-shared/models/wallet.model';
import { MerchantService, MerchantUpdate } from '@olv/ui-shared/services/merchant.service';
import { isMerchantDisabledError } from '@olv/ui-shared/services/merchant-access';
import { WalletService } from '@olv/ui-shared/services/wallet.service';
import { FinancialSummaryHeaderComponent } from '@olv/ui-shared/ui-shared/financial-summary-header/financial-summary-header.component';
import { BalanceBreakdownComponent } from '@olv/ui-shared/ui-shared/balance-breakdown/balance-breakdown.component';
import { AssetBalanceDrawerComponent } from '@olv/ui-shared/ui-shared/asset-balance-drawer/asset-balance-drawer.component';
import { MerchantDepositComponent } from '../deposit/merchant-deposit.component';
import { MerchantEditComponent } from '../merchant-edit/merchant-edit.component';
import { MerchantTransactionHistoryComponent } from '../merchant-transaction-history/merchant-transaction-history.component';
import { MerchantWalletManagementComponent } from '../merchant-wallet-management/merchant-wallet-management.component';
import { MerchantWhitelistedWalletsComponent } from '../merchant-whitelisted-wallets/merchant-whitelisted-wallets.component';
import { MerchantWithdrawComponent } from '../merchant-withdraw/merchant-withdraw.component';
import { AppContextService } from '@olv/ui-shared/services/app-context.service';
import { WalletCheckComponent } from '../wallet-check/wallet-check.component';
import { MatIconModule } from '@angular/material/icon';
import { TabStripDirective } from '@olv/ui-shared/directives/tab-strip.directive';

interface EntityData {
  walletBalances?: WalletBalances;
}

const SHOW_ASSETS_TABS = ['Transaction History', 'Wallet', 'Deposit', 'Withdraw'];

@Component({
  selector: 'app-merchant-details',
  standalone: true,
  imports: [
    TabStripDirective,
    CommonModule,
    RouterLink,
    FinancialSummaryHeaderComponent,
    BalanceBreakdownComponent,
    AssetBalanceDrawerComponent,
    MerchantTransactionHistoryComponent,
    MerchantDepositComponent,
    MerchantWithdrawComponent,
    MerchantWalletManagementComponent,
    MerchantEditComponent,
    MerchantWhitelistedWalletsComponent,
    WalletCheckComponent,
    HasPermissionDirective,
    MatIconModule,
    LoadingSpinnerComponent,
  ],
  templateUrl: './merchant-details.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./merchant-details.component.scss'],
})
export class MerchantDetailsComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private walletService = inject(WalletService);
  private merchantService = inject(MerchantService);
  private location = inject(Location);

  protected readonly Resources = Resources;
  protected readonly Scopes = Scopes;
  private readonly appCtx = inject(AppContextService);
  private readonly router = inject(Router);
  private readonly toastr = inject(ToastService);

  /**
   * Expose the shared logo signal via a getter to avoid using the service before init
   */
  get logoSignal() {
    return this.merchantService.logoSignal;
  }
  merchantId: string | null = '';
  numericMerchantId: number | null = null;
  merchantName = '';
  entityData: EntityData = {
    walletBalances: {
      baseCurrency: 'USD',
      baseCurrencyDisplayPrecision: 2,
      totalBalanceInBaseCurrency: 0,
      availableBalanceInBaseCurrency: 0,
      currencyBalances: [],
    },
  };
  activeTab = 'Balance';
  merchant: Merchant = {} as Merchant;
  logoBase64?: string;

  merchantWallets: WalletDto[] = [];
  merchantApiWallets: WalletDto[] = [];
  totalWallets = 0;
  oneTimeWalletsCount = 0;
  apiWalletsCount = 0;
  hasMoreApiWallets = false;
  isLoadingWallets = false;
  walletsError: string | null = null;
  isDisableFilters = false;
  hasMoreWallets = false;
  isFirstLoad = true;
  initialized = false;
  isLoadingMerchant = false;
  balanceError: string | null = null;
  /**
   * Settlement portal only: tab content stays hidden until the merchant has loaded. A disabled merchant is refused by
   * the API (403) unless the viewer has the AccessDisabled scope. This check used to live in a route guard, which held
   * the whole navigation on the slow merchant request and left the list page on screen with no feedback.
   */
  hasMerchantAccess = false;
  readonly DEFAULT_WALLET_STATUS: WalletStatusEnum[] = [WalletStatusEnum.Active];

  private destroy$ = new Subject<void>();
  private handledMerchantUpdate: MerchantUpdate | null;
  private handledDetailRefresh: number;

  // Placeholder data for summary
  totalDeposits = '$0.00'; // Using period for consistency
  totalWithdraw = '$0.00'; // Using period for consistency

  // Placeholder flags/data for empty states
  hasPendingWalletActions = false; // Set to true if there are actions
  hasPendingTransactions = false; // Set to true if there are transactions
  transactionHistorySearch = '';

  isSettlementApp = false; // Flag to indicate if the current app is merchant or not

  readonly sideComponentOpen = signal(false); // Flag to control the sidebar component

  assetsDrawerOpen = false;

  get showAssetsButton(): boolean {
    return SHOW_ASSETS_TABS.includes(this.activeTab);
  }

  toggleAssetsDrawer(): void {
    this.assetsDrawerOpen = !this.assetsDrawerOpen;
  }
  constructor() {
    // Both signals keep their last value across visits, and the first effect also reruns on logo and sidebar
    // changes, so only a new value may trigger a request. Otherwise a stale value re-fetches the (slow) merchant
    // right after the page's own load.
    this.handledMerchantUpdate = this.merchantService.merchantUpdatedSignal();
    this.handledDetailRefresh = this.merchantService.merchantDetailRefreshSignal();

    // React to any logo updates via the shared signal
    effect(() => {
      const updatedLogo = this.merchantService.logoSignal();
      if (updatedLogo) {
        this.logoBase64 = updatedLogo;
        if (this.merchant?.logo) {
          this.merchant.logo.fileData = updatedLogo;
        }
      }
      this.sideComponentOpen.set(this.merchantService.sidebarOpenSignal());
      const merchantUpdate = this.merchantService.merchantUpdatedSignal();
      if (merchantUpdate !== this.handledMerchantUpdate) {
        this.handledMerchantUpdate = merchantUpdate;
        if (merchantUpdate?.merchantId === this.merchantId) {
          this.loadMerchant(); // Re-fetch the updated merchant
        }
      }
    });

    effect(() => {
      const refreshTick = this.merchantService.merchantDetailRefreshSignal();
      if (refreshTick !== this.handledDetailRefresh) {
        this.handledDetailRefresh = refreshTick;
        if (this.initialized) {
          this.refreshMerchantBalance();
        }
      }
    });
  }

  ngOnInit(): void {
    const appName = this.appCtx.getAppName();
    this.isSettlementApp = appName === 'settlement' ? true : false;
    this.merchantId = this.route.snapshot.paramMap.get('id');
    console.log('Merchant ID:', this.merchantId);
    // Convert merchantId to a number
    this.numericMerchantId = this.merchantId ? +this.merchantId : null;
    console.log('Numeric Merchant ID:', this.numericMerchantId);
    // TODO: Fetch actual merchant data based on the merchantId
    const fromWalletDetails = window.history.state['fromWalletDetails'];

    // Load merchant wallets if merchantId is available
    if (this.merchantId) {
      this.loadMerchantWallets();
      this.loadMerchant();
    } else if (!this.isSettlementApp) {
      // Merchant portal: no id in the route, the API resolves the signed-in merchant
      this.loadMerchant();
    }

    if (fromWalletDetails) {
      this.selectTab('Wallet');
    }

    const transactionId = this.route.snapshot.queryParamMap?.get('transactionId');
    if (transactionId) {
      this.transactionHistorySearch = transactionId;
      this.selectTab('Transaction History');
      if (this.merchantId) {
        this.location.replaceState(`/merchants/${this.merchantId}`);
      }
    }
  }

  /**
   * Load wallets for the current merchant
   */
  loadMerchantWallets(): void {
    if (!this.merchantId) {
      this.walletsError = 'No merchant ID available';
      return;
    }

    this.isLoadingWallets = true;
    this.walletsError = null;
    const filter1: WalletFilterParams = {
      status: [...this.DEFAULT_WALLET_STATUS],
      skipCount: 0,
      // UnsupportedFunds rides along with the pool wallets: it renders inside the Pool Wallets section, and this
      // filtered fetch is the only thing that decides whether the group can appear at all.
      walletType: [WalletTypeEnum.Pooled, WalletTypeEnum.Dedicated, WalletTypeEnum.UnsupportedFunds],
    };

    const filter2: WalletFilterParams = {
      status: [...this.DEFAULT_WALLET_STATUS],
      skipCount: 0,
      walletType: [2],
      maxResultCount: 10,
    };

    const apiFilter: WalletFilterParams = {
      status: [...this.DEFAULT_WALLET_STATUS],
      skipCount: 0,
      maxResultCount: 10,
    };

    // All three requests fire in parallel; pooled + dedicated share one call while
    // one-time and API wallets are fetched separately because they are paginated
    forkJoin({
      poolAndDedicated: this.walletService.getMerchantWallets(this.merchantId, filter1),
      oneTime: this.walletService.getMerchantWallets(this.merchantId, filter2),
      api: this.walletService.getMerchantApiWallets(this.merchantId, apiFilter),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ poolAndDedicated, oneTime, api }) => {
          this.merchantWallets = [...poolAndDedicated.items, ...oneTime.items];
          this.merchantApiWallets = api.items;
          this.totalWallets = poolAndDedicated.totalCount + oneTime.totalCount + api.totalCount;
          this.isDisableFilters = this.isFirstLoad && this.merchantWallets.length === 0 && api.items.length === 0;
          this.hasMoreWallets = oneTime.items.length < oneTime.totalCount;
          this.oneTimeWalletsCount = oneTime.totalCount;
          this.apiWalletsCount = api.totalCount;
          this.hasMoreApiWallets = api.items.length < api.totalCount;
          this.isFirstLoad = false;
          this.isLoadingWallets = false;
        },
        error: (err) => {
          console.error('Error loading merchant wallets:', err);
          this.walletsError = 'Failed to load merchant wallets. Please try again.';
          this.isLoadingWallets = false;
        },
      });
  }
  /**
   * Load merchant portal wallets
   */
  loadMerchantPortalWallets(): void {
    this.isLoadingWallets = true;
    this.walletsError = null;

    this.walletService
      .getMerchantPortalWallets()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: PagedResultDto<WalletDto>) => {
          console.log('Merchant wallets loaded:', response);
          this.merchantWallets = response.items;
          this.totalWallets = response.totalCount;
          this.isLoadingWallets = false;
        },
        error: (err) => {
          console.error('Error loading merchant wallets:', err);
          this.walletsError = 'Failed to load merchant wallets. Please try again.';
          this.isLoadingWallets = false;
        },
      });
  }

  loadMerchant(): void {
    this.isLoadingMerchant = true;
    const merchant$ = this.isSettlementApp
      ? this.merchantService.getMerchantById(this.merchantId!)
      : this.merchantService.getMerchantWithoutId();

    merchant$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (merchant) => {
        this.hasMerchantAccess = true;
        this.merchant = merchant;
        this.merchantName = merchant.name;

        if (merchant.walletBalances) {
          this.entityData = { walletBalances: this.getWalletBalances(merchant.walletBalances) };
        }

        if (merchant.logo && merchant.logo.fileData) {
          this.logoBase64 = merchant.logo.fileData;
        }

        this.balanceError = null;
        this.initialized = true;
        this.isLoadingMerchant = false;
      },
      error: (err) => {
        console.error('Error loading merchant:', err);
        this.balanceError = 'Failed to load merchant balance. Please try again.';
        this.isLoadingMerchant = false;
        // The API refuses a disabled merchant with a 403 unless the viewer has AccessDisabled; the request is marked
        // so that 403 reaches here rather than logging the user out
        this.toastr.error(
          isMerchantDisabledError(err)
            ? 'This merchant is disabled and you do not have access to disabled merchants.'
            : 'Failed to load merchant details. Please try again.',
        );
        // A failed first load goes back to the list, as the old route guard did; a failed reload keeps the page,
        // unless the merchant was disabled meanwhile: this viewer may no longer see it, so its tabs must not stay up
        if (this.isSettlementApp && (!this.initialized || isMerchantDisabledError(err))) {
          this.hasMerchantAccess = false;
          this.leaveToMerchantList();
        }
        this.initialized = true;
      },
    });
  }

  /** replaceUrl keeps the blocked details URL out of history, as the old guard redirect did. */
  private leaveToMerchantList(): void {
    this.router.navigate(['/merchants'], { replaceUrl: true });
  }

  refreshMerchantBalance(): void {
    if (!this.merchantId) {
      return;
    }

    // An explicit refresh follows an action that moved funds, so skip the backend's cached header totals
    this.merchantService
      .getMerchantBalanceById(this.merchantId, true)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (walletBalances: WalletBalances) => {
          this.entityData = { walletBalances: this.getWalletBalances(walletBalances) };
          this.balanceError = null;
        },
        error: () => {
          this.balanceError = 'Failed to load merchant balance. Please try again.';
        },
      });
  }

  private getWalletBalances(balance: WalletBalances): WalletBalances {
    return {
      baseCurrency: balance.baseCurrency,
      baseCurrencyDisplayPrecision: balance.baseCurrencyDisplayPrecision,
      totalBalanceInBaseCurrency: balance.totalBalanceInBaseCurrency,
      availableBalanceInBaseCurrency: balance.availableBalanceInBaseCurrency,
      currencyBalances: balance.currencyBalances.map((b) => ({
        currencyId: b.currencyId,
        currencySymbol: b.currencySymbol,
        currencyName: b.currencyName,
        currencyDisplayPrecision: b.currencyDisplayPrecision,
        totalAmountInCurrency: b.totalAmountInCurrency,
        availableAmountInCurrency: b.availableAmountInCurrency,
        availableBalanceInBaseCurrency: b.availableBalanceInBaseCurrency,
        totalBalanceInBaseCurrency: b.totalBalanceInBaseCurrency,
        riskDistribution: b.riskDistribution,
      })),
    };
  }

  selectTab(tabName: string): void {
    this.activeTab = tabName;
    this.sideComponentOpen.set(false);
    this.merchantService.closeSidebar();
    if (!this.showAssetsButton) {
      this.assetsDrawerOpen = false;
    }

    // Handle specific tab selections
    if (tabName === 'Wallet') {
      console.log('Wallet tab selected with merchant ID:', this.merchantId);
      // Ensure merchantId is available for the wallet management component
      if (!this.merchantId) {
        console.error('No merchant ID available for wallet management');
      }
    }

    // Load wallets data when the Deposit or Withdraw tab is selected
    if ((tabName === 'Deposit' || tabName === 'Withdraw') && this.merchantId) {
      // Reload wallets if needed
      if (this.merchantWallets.length === 0 && !this.isLoadingWallets) {
        this.loadMerchantWallets();
      }
    }
  }

  /**
   * Handle wallet creation event from wallet management component
   * This refreshes the wallet list for all components that need it
   */
  onWalletCreated(walletId: string): void {
    console.log(`Wallet created with ID: ${walletId}, refreshing wallet lists for all components`);

    // Reload merchant wallets to update deposit and withdraw components
    if (this.merchantId) {
      // Add a small delay to ensure the backend has time to process the new wallet
      setTimeout(() => {
        this.loadMerchantWallets();
      }, 500);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
